/**
 * @file Settings-owned checklist template modal form helpers.
 */

import { appData } from "../../app/state/state.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
  FIELD_LABEL_CLASSES,
  FIELD_WRAPPER_CLASSES,
  INLINE_EDIT_INPUT_CLASSES,
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
  NATIVE_INPUT_CLASSES,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
  getNativeFieldWrapperHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  registerControl,
} from "../../shared/ui/controls/controlCore.js";
import {
  createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
  applyBrandingVariables,
} from "../../shared/ui/brandingTokens.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const ENTITY_OPTIONS = [
  { value: "task", label: "Tasks", typeConfigKey: "taskTypes" },
  { value: "patient", label: "Patients", typeConfigKey: "patientTypes" },
  { value: "lab", label: "Labs", typeConfigKey: "labTypes" },
  { value: "project", label: "Projects", typeConfigKey: "projectTypes" },
  { value: "asset", label: "Assets", typeConfigKey: "assetTypes" },
  { value: "maintenance", label: "Maintenance", typeConfigKey: "maintenanceTypes" },
];

function toArray(value) {
  return Array.isArray(value) ? value : [value].filter(Boolean);
}

function attrValue(value = "") {
  return escapeHTML(String(value ?? ""));
}

function renderNativeCheckbox({
  checked = false,
  disabled = false,
  label,
  name,
  value,
  dataAttrs = "",
  labelClass = "",
}) {
  const disabledAttr = disabled ? " disabled" : "";
  const checkedAttr = checked ? " checked" : "";
  const disabledClass = disabled ? " opacity-50 cursor-not-allowed" : "";
  return `
    <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} ${labelClass}${disabledClass}" data-native-checkbox-label>
      <input type="checkbox" name="${name}" value="${attrValue(value)}" class="${NATIVE_CHECKBOX_CLASSES}"${checkedAttr}${disabledAttr}${dataAttrs}>
      <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
      </span>
      <span class="min-w-0 break-words leading-tight">${escapeHTML(label)}</span>
    </label>
  `;
}

function getAllowedEntityValues(scope) {
  const allBoards = scope.querySelector("#all-boards-checkbox");
  if (allBoards?.checked) {return ["all"];}
  return Array.from(scope.querySelectorAll("[data-entity-checkbox]"))
    .filter((checkbox) => checkbox.checked)
    .map((checkbox) => checkbox.value);
}

function getEffectiveTypeEntities(allowedEntities) {
  const allowed = toArray(allowedEntities);
  if (allowed.includes("all")) {
    return ENTITY_OPTIONS.map((option) => option.value);
  }
  return allowed.filter((value) =>
    ENTITY_OPTIONS.some((option) => option.value === value));
}

function getLinkedTypeEntityMap() {
  const map = new Map();
  ENTITY_OPTIONS.forEach((entity) => {
    const types = appData?.config?.[entity.typeConfigKey] || [];
    types.forEach((type) => {
      if (!type?.id) {return;}
      const typeId = String(type.id);
      const entities = map.get(typeId) || new Set();
      entities.add(entity.value);
      map.set(typeId, entities);
    });
  });
  return map;
}

function createLinkedTypeOptions(selectedValues = [], allowedEntities = []) {
  const selectedSet = new Set(toArray(selectedValues).map((value) => String(value)));
  const allowedSet = new Set(getEffectiveTypeEntities(allowedEntities));
  const seen = new Set();
  const options = [];

  ENTITY_OPTIONS.forEach((entity, entityIndex) => {
    const isEntityAllowed = allowedSet.has(entity.value);
    const types = appData?.config?.[entity.typeConfigKey] || [];
    types.forEach((type, typeIndex) => {
      if (!type?.id) {return;}
      const value = String(type.id);
      const isSelected = selectedSet.has(value);
      if (seen.has(value) || (!isEntityAllowed && !isSelected)) {return;}
      seen.add(value);

      const isArchived = type.isActive === false;
      options.push({
        value,
        label: type.name || value,
        group: `${entity.label} Types`,
        order: entityIndex * 1000 + typeIndex,
        archived: isArchived,
        selectedOnly: isSelected && !isEntityAllowed,
        unavailableLabel: !isEntityAllowed ? "not in selected boards" :
          (isArchived ? "archived" : ""),
        source: type,
      });
    });
  });

  selectedSet.forEach((value) => {
    if (seen.has(value)) {return;}
    options.push({
      value,
      label: value,
      group: "Saved linked types",
      order: 9999,
      selectedOnly: true,
      unavailableLabel: "unavailable",
    });
  });

  return options;
}

function getAllLinkedTypeValues(allowedEntities = []) {
  return createLinkedTypeOptions([], allowedEntities)
    .filter((option) => !option.selectedOnly && !option.disabled && !option.archived)
    .map((option) => String(option.value));
}

function hasEveryLinkedTypeSelected(selectedValues = [], allValues = []) {
  const selected = toArray(selectedValues).map((value) => String(value));
  if (selected.length !== allValues.length) {return false;}
  const selectedSet = new Set(selected);
  return allValues.every((value) => selectedSet.has(String(value)));
}

/**
 * Generates HTML fields for the Checklist Template form shell.
 * @param {object} itemData Checklist template draft.
 * @returns {string} Checklist template form markup.
 */
export function getChecklistTemplateFormFields(itemData) {
  const val = (field, def) => itemData?.[field] ?? def;

  const allowed = val("allowedEntities", []);
  const isAll = allowed.includes("all") ||
    (itemData?.id && allowed.length === 0);

  return `
    <h4 class="font-semibold text-lg text-gray-700 dark:text-gray-300 border-b pb-2 dark:border-gray-600">Template Properties</h4>
    <div class="grid grid-cols-1 gap-4 mt-4">
      ${getNativeFieldWrapperHtml("Template Name", `
        <input type="text" name="name" value="${attrValue(val("name", ""))}" class="${NATIVE_INPUT_CLASSES}" autocomplete="off" required>
      `, { required: true })}

      <div id="allowed-entities-control" class="${FIELD_WRAPPER_CLASSES}">
        ${getFieldLabelHtml("Boards", true)}
        <div id="allowed-entities-rows" class="grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-white p-3 sm:grid-cols-2 dark:border-gray-600 dark:bg-gray-700">
          ${renderNativeCheckbox({
            checked: isAll,
            label: "All Boards",
            name: "allowedEntities",
            value: "all",
            dataAttrs: ' id="all-boards-checkbox"',
          })}
          ${ENTITY_OPTIONS.map((entity) => renderNativeCheckbox({
            checked: allowed.includes(entity.value) && !isAll,
            disabled: isAll,
            label: entity.label,
            name: "allowedEntities",
            value: entity.value,
            dataAttrs: " data-entity-checkbox",
          })).join("")}
        </div>
        <p id="allowed-entities-validation" class="mt-1 hidden text-xs font-medium text-red-600" aria-live="polite"></p>
      </div>

      <div id="linked-types-container" class="${FIELD_WRAPPER_CLASSES}">
        <label class="${FIELD_LABEL_CLASSES}" for="linked-types-control">Linked Types</label>
        <div id="linked-types-control"></div>
      </div>

      <div class="${FIELD_WRAPPER_CLASSES}">
        <div class="flex min-h-10 items-center gap-3 sm:min-h-[30px]">
          <label class="toggle-switch shrink-0">
            <input type="checkbox" name="isManual" aria-label="Manual Append Only" ${val("isManual", false) ? "checked" : ""}>
            <span class="slider"></span>
          </label>
          <div class="min-w-0">
            <span class="block text-base font-medium text-gray-700 sm:text-sm dark:text-gray-300">Manual Append Only</span>
          </div>
        </div>
        <p class="mt-1 text-sm leading-5 text-gray-500 sm:text-xs dark:text-gray-400">When toggled, template will not be automatically applied if the specified types are selected by users</p>
        <input type="hidden" name="sortOrder" value="${attrValue(val("sortOrder", 1))}">
      </div>

      <div class="border-t pt-4 border-gray-200 dark:border-gray-600">
        <div class="mb-3">
          <h4 class="font-semibold text-lg text-gray-700 dark:text-gray-300">Checklist Items</h4>
        </div>

        <div id="template-items-list" class="max-h-80 overflow-y-auto pr-2 pb-1">
          <!-- Items rendered here by JS -->
        </div>
        <div class="flex min-h-10 items-start gap-2 py-1 sm:min-h-7 sm:py-0">
          <div class="mt-2 flex h-5 w-6 shrink-0 items-center justify-center sm:mt-1">
            <span class="material-symbols-outlined text-[18px] leading-none text-[var(--dfc-brand-accent)]">add</span>
          </div>
          <div class="min-w-0 flex-1">
            <input type="text" id="template-item-ghost-input" class="${INLINE_EDIT_INPUT_CLASSES} block box-border min-h-10 text-base leading-7 !py-1 sm:min-h-7 sm:text-sm sm:leading-5" placeholder="Add a new item">
          </div>
        </div>

        <!-- Hidden input to store JSON payload -->
        <input type="hidden" name="itemsJson" id="template-items-json" value="">
      </div>
    </div>
  `;
}

/**
 * Wires checklist template-specific modal behavior.
 * @param {HTMLElement} formFields Form fields container.
 * @param {object} formItemData Current modal form item data.
 */
export function setupChecklistTemplateForm(
  formFields,
  formItemData,
) {
  applyBrandingVariables(formFields.closest("#form-modal") || formFields, appData);

  const allBoardsCb = formFields.querySelector("#all-boards-checkbox");
  const entityCheckboxes = Array.from(formFields.querySelectorAll(
    "[data-entity-checkbox]",
  ));
  const allowedRoot = formFields.querySelector("#allowed-entities-control");
  const allowedRows = formFields.querySelector("#allowed-entities-rows");
  const allowedMessage = formFields.querySelector("#allowed-entities-validation");
  const linkedTypesRoot = formFields.querySelector("#linked-types-control");
  const linkedTypeEntityMap = getLinkedTypeEntityMap();
  let linkedTypesControl = null;
  let activeLinkedAllowedValues = getAllowedEntityValues(formFields);

  const syncAllowedInvalidState = () => {
    const isInvalid = allowedRoot?.hasAttribute("data-control-invalid");
    allowedRows?.classList.toggle("border-red-600", Boolean(isInvalid));
    allowedRows?.classList.toggle("border-gray-200", !isInvalid);
    if (allowedRows) {
      allowedRows.style.boxShadow = isInvalid ?
        "0 0 0 1.5px rgba(220, 38, 38, 0.22)" :
        "";
    }
  };

  const allowedObserver = allowedRoot ?
    new MutationObserver(syncAllowedInvalidState) :
    null;
  allowedObserver?.observe(allowedRoot, {
    attributes: true,
    attributeFilter: ["data-control-invalid"],
  });

  const allowedControl = allowedRoot ? registerControl({
    root: allowedRoot,
    name: "allowedEntities",
    multiple: true,
    mirror: false,
    value: getAllowedEntityValues(formFields),
    getValue: () => getAllowedEntityValues(formFields),
    validate: (value) => ({
      valid: Array.isArray(value) && value.length > 0,
      message: "Select at least one board.",
    }),
    validationMessageElement: allowedMessage,
    destroy: () => allowedObserver?.disconnect(),
  }) : null;

  const getLinkedValue = () => toArray(linkedTypesControl?.value);

  const isLinkedTypeAllowed = (value, effectiveEntities) => {
    const entities = linkedTypeEntityMap.get(String(value));
    if (!entities) {return true;}
    return effectiveEntities.some((entity) => entities.has(entity));
  };

  const syncLinkedTypeOptions = () => {
    if (!linkedTypesControl) {return;}
    const allowedValues = getAllowedEntityValues(formFields);
    const previousAllValues = getAllLinkedTypeValues(activeLinkedAllowedValues);
    const effectiveEntities = getEffectiveTypeEntities(allowedValues);
    const currentValue = getLinkedValue();
    const wasAllTypes = currentValue.length === 0 ||
      hasEveryLinkedTypeSelected(currentValue, previousAllValues);
    const nextValue = wasAllTypes ?
      getAllLinkedTypeValues(allowedValues) :
      currentValue.filter((value) =>
        isLinkedTypeAllowed(value, effectiveEntities));
    activeLinkedAllowedValues = allowedValues;
    linkedTypesControl.update({
      options: createLinkedTypeOptions(nextValue, allowedValues),
    });
    if (nextValue.length !== currentValue.length ||
        nextValue.some((value, index) => value !== currentValue[index])) {
      linkedTypesControl.setValue(nextValue);
    }
  };

  if (linkedTypesRoot) {
    const initialAllowedValues = getAllowedEntityValues(formFields);
    const initialLinkedTypes = toArray(formItemData.linkedTypes);
    const initialValue = initialLinkedTypes.length > 0 ?
      initialLinkedTypes :
      getAllLinkedTypeValues(initialAllowedValues);
    activeLinkedAllowedValues = initialAllowedValues;
    linkedTypesControl = createSelectControl({
      root: linkedTypesRoot,
      name: "linkedTypes",
      value: initialValue,
      options: createLinkedTypeOptions(initialValue, initialAllowedValues),
      multiple: true,
      checkboxMenu: true,
      bulkSelection: true,
      searchable: true,
      searchThreshold: 7,
      mirror: false,
      portalMenu: true,
      placeholder: "+ Select linked types",
      emptySelectionLabel: "All types",
      allSelectedLabel: "All types",
      selectedCountLabel: "Linked types",
      searchPlaceholder: "Search types",
      emptyMessage: "No types available for the selected boards.",
      accessibleName: "Linked types",
    });
  }

  const syncBoardCheckboxState = () => {
    const isAll = Boolean(allBoardsCb?.checked);
    entityCheckboxes.forEach((cb) => {
      cb.disabled = isAll;
      if (isAll) {cb.checked = false;}
      const label = cb.closest("[data-native-checkbox-label]");
      label?.classList.toggle("opacity-50", isAll);
      label?.classList.toggle("cursor-not-allowed", isAll);
    });
    allowedControl?.setValue(getAllowedEntityValues(formFields));
    if (allowedRoot?.hasAttribute("data-control-invalid")) {
      allowedControl?.validate();
    }
    syncAllowedInvalidState();
    syncLinkedTypeOptions();
  };

  if (allBoardsCb) {
    allBoardsCb.addEventListener("change", syncBoardCheckboxState);
  }

  entityCheckboxes.forEach((cb) => {
    cb.addEventListener("change", () => {
      if (cb.checked && allBoardsCb && allBoardsCb.checked) {
        allBoardsCb.checked = false;
      }
      syncBoardCheckboxState();
    });
  });

  syncBoardCheckboxState();

  const itemsList = formFields.querySelector("#template-items-list");
  const ghostInput = formFields.querySelector("#template-item-ghost-input");
  const jsonInput = formFields.querySelector("#template-items-json");
  if (!itemsList || !ghostInput || !jsonInput) {return;}

  let templateItems = Object.entries(formItemData.items || {})
    .map(([id, item]) => ({
      id: item?.id || id,
      ...item,
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const updateJson = () => {
    const itemsObj = {};
    templateItems.forEach((templateItem, index) => {
      templateItem.order = index;
      itemsObj[templateItem.id] = templateItem;
    });
    jsonInput.value = JSON.stringify(itemsObj);
  };

  const renderItems = () => {
    itemsList.innerHTML = "";
    if (templateItems.length === 0) {
      updateJson();
      bindNativeFieldBehavior(itemsList);
      return;
    }

    templateItems.forEach((templateItem, index) => {
      const row = document.createElement("div");
      row.className =
        "group flex min-h-10 items-start gap-2 py-1 sm:min-h-7 sm:py-0";
      row.innerHTML = `
        <div class="mt-2.5 flex h-5 w-6 shrink-0 items-center justify-center text-xs font-medium tabular-nums text-gray-400 sm:mt-1">
          ${index + 1}
        </div>
        <div class="min-w-0 flex-1">
          <input type="text" class="item-text-input ${INLINE_EDIT_INPUT_CLASSES} block box-border min-h-10 text-base leading-7 !py-1 sm:min-h-7 sm:text-sm sm:leading-5" value="" required>
        </div>
        <div class="flex h-10 shrink-0 items-center gap-0.5 opacity-100 sm:h-7 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
          <button type="button" class="move-up-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" ${index === 0 ? "disabled" : ""} title="Move Up" aria-label="Move item up">
            <span class="material-symbols-outlined text-[18px] pointer-events-none">expand_less</span>
          </button>
          <button type="button" class="move-down-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" ${index === templateItems.length - 1 ? "disabled" : ""} title="Move Down" aria-label="Move item down">
            <span class="material-symbols-outlined text-[18px] pointer-events-none">expand_more</span>
          </button>
          <button type="button" class="delete-item-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:text-red-600" title="Remove Item" aria-label="Remove item">
            <span class="material-symbols-outlined text-[18px] pointer-events-none">close</span>
          </button>
        </div>
      `;

      const textInput = row.querySelector(".item-text-input");
      textInput.value = templateItem.text || "";

      textInput.addEventListener("input", (event) => {
        templateItem.text = event.target.value;
        updateJson();
      });

      textInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          ghostInput.focus();
        }
      });

      row.querySelector(".move-up-btn").addEventListener("click", () => {
        if (index > 0) {
          const temp = templateItems[index];
          templateItems[index] = templateItems[index - 1];
          templateItems[index - 1] = temp;
          renderItems();
        }
      });

      row.querySelector(".move-down-btn").addEventListener("click", () => {
        if (index < templateItems.length - 1) {
          const temp = templateItems[index];
          templateItems[index] = templateItems[index + 1];
          templateItems[index + 1] = temp;
          renderItems();
        }
      });

      row.querySelector(".delete-item-btn").addEventListener("click", () => {
        templateItems = templateItems.filter((item) =>
          item.id !== templateItem.id);
        renderItems();
      });

      itemsList.appendChild(row);
    });
    updateJson();
    bindNativeFieldBehavior(itemsList);
  };

  const addTemplateItem = (text = "") => {
    templateItems.push({
      id: crypto.randomUUID(),
      text,
      order: templateItems.length,
    });
    renderItems();
  };

  const commitGhostInput = ({ refocus = false } = {}) => {
    const text = ghostInput.value.trim();
    if (!text) {return false;}
    addTemplateItem(text);
    ghostInput.value = "";
    if (refocus) {ghostInput.focus();}
    return true;
  };

  ghostInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitGhostInput({ refocus: true });
    }
  });

  ghostInput.addEventListener("blur", () => {
    commitGhostInput();
  });

  renderItems();
  bindNativeFieldBehavior(formFields.closest("form") || formFields);
}

/**
 * Opens the checklist template modal.
 * @param {object | null} item Existing template or initial data.
 * @returns {void}
 */
export function openChecklistTemplateForm(item = null) {
  openStandardForm("checklist_template", item, {
    getFieldsHtml: getChecklistTemplateFormFields,
    afterRender: ({ formFields, formItemData }) => {
      setupChecklistTemplateForm(formFields, formItemData);
    },
  });
}
